import { z } from "zod";
import { WorkforceRoleSchema } from "./actor.js";
import { ClinicalSourceSchema } from "./clinical.js";
import { StructuredPrescriptionPayloadSchema } from "./medications.js";
import { StructuredImagingOrderPayloadSchema, StructuredLabOrderPayloadSchema } from "./orders.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, JsonValueSchema, Sha256Schema, } from "./primitives.js";
import { isManualPrintReferral, isStaffSelectedFaxReferral, ReferralAttachmentBindingSchema, ReferralDeliveryModeSchema, ReferralDestinationModeSchema, ReferralRoutingAuthorizationSchema, } from "./referral.js";
import { ValidationIssueSchema } from "./validation.js";
export { ValidationIssueSchema } from "./validation.js";
export const RiskTierSchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
]);
export const ApprovalClassSchema = z.enum([
    "none",
    "policy",
    "user",
    "clinician",
    "epcs",
    "prohibited",
]);
export const ActionStatusSchema = z.enum([
    "draft",
    "validated",
    "pending_approval",
    "approved",
    "queued",
    "dispatched",
    "acknowledged",
    "closed",
    "changes_requested",
    "failed",
    "cancelled",
    "expired",
]);
export const ClinicalActionKindSchema = z.enum([
    "referral",
    "lab_order",
    "imaging_order",
    "prescription",
    "discharge_instruction",
    "follow_up",
    "billing_suggestion",
    "patient_plan",
    "form",
    "scheduling",
    "staff_task",
]);
const baseActionShape = {
    id: IdSchema,
    tenantId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    version: z.number().int().positive(),
    status: ActionStatusSchema,
    riskTier: RiskTierSchema,
    approvalClass: ApprovalClassSchema,
    summary: z.string().min(1).max(500),
    rationale: z.string().min(1).max(2_000),
    evidence: z.array(ClinicalSourceSchema).max(50),
    warnings: z.array(z.string().min(1).max(500)).optional(),
    createdAt: IsoDateTimeSchema,
    createdBy: z.enum(["agent", "human"]),
    agentRunId: IdSchema.optional(),
    agentRunContentHash: Sha256Schema.optional(),
};
export const ReferralActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("referral"),
    payload: z
        .object({
        specialtyCode: z.string().min(1).max(80),
        serviceCodes: z.array(z.string().min(1).max(80)).max(100).optional(),
        reason: z.string().min(1).max(2_000),
        referralQuestion: z.string().min(1).max(4_000).optional(),
        clinicalRationale: z.string().min(1).max(8_000).optional(),
        urgency: z.enum(["routine", "urgent", "emergent"]).optional(),
        diagnosisCodes: z.array(z.string().min(1).max(80)).min(1),
        relevantHistory: z.array(z.string().min(1).max(2_000)).max(100).optional(),
        medications: z.array(z.string().min(1).max(500)).max(200).optional(),
        allergies: z.array(z.string().min(1).max(500)).max(200).optional(),
        problems: z.array(z.string().min(1).max(500)).max(200).optional(),
        formAnswers: z.record(z.string().min(1).max(120), z.string().max(4_000)).optional(),
        attachmentIds: z.array(IdSchema),
        attachmentBindings: z.array(ReferralAttachmentBindingSchema).max(50).optional(),
        deliveryMode: ReferralDeliveryModeSchema.optional(),
        destinationMode: ReferralDestinationModeSchema.optional(),
        destinationId: IdSchema.optional(),
        routingAuthorization: ReferralRoutingAuthorizationSchema.optional(),
        consentEvidenceId: IdSchema.optional(),
        externalTransmissionEnabled: z.boolean().optional(),
    })
        .strict()
        .superRefine((payload, context) => {
        const mode = payload.destinationMode;
        const manualPrint = isManualPrintReferral(payload);
        const staffSelected = isStaffSelectedFaxReferral(payload);
        if (payload.externalTransmissionEnabled &&
            mode !== "staff_selected" &&
            mode !== "automatic") {
            context.addIssue({
                code: "custom",
                path: ["externalTransmissionEnabled"],
                message: "Fax sending requires an explicit policy-bound routing mode",
            });
        }
        if (mode === "staff_selected" && !staffSelected) {
            context.addIssue({
                code: "custom",
                path: ["destinationMode"],
                message: "Staff-selected eFax approval must not preselect a destination",
            });
        }
        if (mode === undefined && payload.destinationId === undefined) {
            context.addIssue({
                code: "custom",
                path: ["destinationId"],
                message: "Legacy referral actions require an explicit destination",
            });
        }
        // A clinician-selected referral binds a verified directory destination,
        // except under prepare-only delivery where the clinic prints and sends
        // the letter itself (manual print) and no electronic destination exists.
        if (mode === "clinician_selected" && payload.destinationId === undefined && !manualPrint) {
            context.addIssue({
                code: "custom",
                path: ["destinationId"],
                message: "Clinician-selected referrals require an exact destination",
            });
        }
        if ((manualPrint || staffSelected || payload.externalTransmissionEnabled) &&
            payload.routingAuthorization === undefined) {
            context.addIssue({
                code: "custom",
                path: ["routingAuthorization"],
                message: "Destination-later and electronic referrals bind the exact clinic policy version",
            });
        }
        if (mode === "automatic" && payload.destinationId !== undefined) {
            context.addIssue({
                code: "custom",
                path: ["destinationId"],
                message: "Automatic routing approval must not preselect a destination",
            });
        }
        if (mode === "automatic" && payload.routingAuthorization === undefined) {
            context.addIssue({
                code: "custom",
                path: ["routingAuthorization"],
                message: "Automatic routing requires an exact bounded routing authorization",
            });
        }
        if (mode !== undefined &&
            (payload.deliveryMode === undefined ||
                payload.externalTransmissionEnabled === undefined ||
                (payload.externalTransmissionEnabled && payload.deliveryMode !== "efax"))) {
            context.addIssue({
                code: "custom",
                path: ["externalTransmissionEnabled"],
                message: "Only eFax referrals may explicitly authorize electronic transmission",
            });
        }
        if (mode !== undefined) {
            const bindingIds = payload.attachmentBindings?.map((binding) => binding.attachmentId);
            if (bindingIds === undefined ||
                bindingIds.length !== payload.attachmentIds.length ||
                bindingIds.some((id, index) => id !== payload.attachmentIds[index])) {
                context.addIssue({
                    code: "custom",
                    path: ["attachmentBindings"],
                    message: "Extended referral approval requires exact ordered attachment versions and hashes",
                });
            }
        }
    }),
});
export const LabOrderActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("lab_order"),
    payload: StructuredLabOrderPayloadSchema,
});
export const ImagingOrderActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("imaging_order"),
    payload: StructuredImagingOrderPayloadSchema,
});
export const PrescriptionActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("prescription"),
    payload: StructuredPrescriptionPayloadSchema,
});
export const DischargeInstructionActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("discharge_instruction"),
    payload: z.union([
        z
            .object({
            payloadVersion: z.literal(1),
            language: z.string().min(2).max(35),
            disposition: z.enum(["home", "transfer", "admission", "left_before_completion"]),
            diagnosisCodes: z.array(z.string().min(1).max(80)).min(1).max(25),
            instructions: z.array(z.string().min(1).max(2_000)).min(1).max(25),
            medicationInstructions: z.array(z.string().min(1).max(1_000)).max(25),
            returnPrecautions: z.array(z.string().min(1).max(1_000)).min(1).max(25),
            emergencyPrecautions: z.array(z.string().min(1).max(1_000)).min(1).max(25),
            followUpInstructions: z.array(z.string().min(1).max(1_000)).min(1).max(25),
            deliveryChannels: z
                .array(z.enum(["portal", "print", "verbal", "secure_message"]))
                .min(1)
                .max(4),
            teachBackPrompt: z.string().min(1).max(1_000),
        })
            .strict(),
        z
            .object({
            instructions: z.array(z.string().min(1).max(2_000)).min(1),
            returnPrecautions: z.array(z.string().min(1).max(1_000)).min(1),
        })
            .strict(),
    ]),
});
export const FollowUpActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("follow_up"),
    payload: z.object({
        ownerRole: WorkforceRoleSchema.exclude(["system_agent"]),
        escalationRole: z.literal("physician"),
        dueAt: IsoDateTimeSchema,
        escalationAt: IsoDateTimeSchema,
        reason: z.string().min(1).max(1_000),
        sourceActionId: IdSchema.optional(),
    }),
});
export const BillingSuggestionActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("billing_suggestion"),
    payload: z.union([
        z
            .object({
            payloadVersion: z.literal(1),
            serviceDate: z.string().date(),
            diagnosisCodes: z.array(z.string().min(1).max(80)).min(1).max(25),
            procedureCodes: z.array(z.string().min(1).max(80)).min(1).max(25),
            diagnosisItems: z
                .array(z
                .object({
                codeSystem: z.literal("ICD-10-CM"),
                code: z.string().min(1).max(80),
                sequence: z.number().int().positive().max(25),
                sourceEvidenceIds: z.array(IdSchema).min(1).max(25),
            })
                .strict())
                .min(1)
                .max(25),
            serviceLines: z
                .array(z
                .object({
                codeSystem: z.enum(["CPT", "HCPCS"]),
                code: z.string().min(1).max(80),
                modifiers: z.array(z.string().min(1).max(8)).max(4),
                units: z.number().int().positive().max(999),
                placeOfService: z.string().regex(/^\d{2}$/),
                sourceEvidenceIds: z.array(IdSchema).min(1).max(25),
            })
                .strict())
                .min(1)
                .max(25),
            feeScheduleVersion: z.string().min(1).max(80),
            documentationStatus: z.enum(["complete", "gaps_identified"]),
            documentationGaps: z.array(z.string().min(1).max(500)).max(25),
            reviewRequired: z.literal(true),
            claimSubmissionAllowed: z.literal(false),
        })
            .strict(),
        z
            .object({
            diagnosisCodes: z.array(z.string().min(1).max(80)),
            procedureCodes: z.array(z.string().min(1).max(80)).min(1),
        })
            .strict(),
    ]),
});
export const PatientPlanActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("patient_plan"),
    payload: z
        .object({
        planVersion: z.literal(1),
        language: z.string().min(2).max(35),
        problemCodes: z.array(z.string().min(1).max(80)).min(1).max(25),
        goals: z
            .array(z
            .object({
            description: z.string().min(1).max(1_000),
            targetDate: z.string().date().optional(),
            measure: z.string().min(1).max(500).optional(),
        })
            .strict())
            .min(1)
            .max(25),
        interventions: z.array(z.string().min(1).max(1_000)).min(1).max(25),
        selfManagementInstructions: z.array(z.string().min(1).max(1_000)).min(1).max(25),
        monitoring: z
            .array(z
            .object({
            measure: z.string().min(1).max(500),
            schedule: z.string().min(1).max(500),
            ownerRole: WorkforceRoleSchema.exclude(["system_agent", "auditor"]),
            escalationCriteria: z.string().min(1).max(1_000),
        })
            .strict())
            .min(1)
            .max(25),
        sharedDecisionMaking: z.string().min(1).max(2_000),
        reviewAt: IsoDateTimeSchema,
    })
        .strict(),
});
export const FormActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("form"),
    payload: z
        .object({
        formDefinitionId: IdSchema,
        formVersion: z.number().int().positive(),
        purpose: z.string().min(1).max(500),
        fieldValues: z.record(z.string().min(1).max(120), JsonValueSchema),
        requiredFieldIds: z.array(z.string().min(1).max(120)).min(1).max(100),
        unansweredRequiredFieldIds: z.array(z.string().min(1).max(120)).max(100),
        renderFormat: z.enum(["pdf", "html", "fhir_questionnaire_response"]),
        signatureRequired: z.boolean(),
        submissionAllowed: z.literal(false),
    })
        .strict(),
});
export const SchedulingActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("scheduling"),
    payload: z
        .object({
        scheduleVersion: z.literal(1),
        siteId: IdSchema,
        appointmentType: z.string().min(1).max(160),
        serviceCode: z.string().min(1).max(80),
        requestedStartAt: IsoDateTimeSchema,
        requestedEndAt: IsoDateTimeSchema,
        durationMinutes: z.number().int().positive().max(1_440),
        reason: z.string().min(1).max(1_000),
        ownerRole: z.literal("front_desk"),
        preferredChannel: z.enum(["phone", "portal", "sms"]),
        bookingMode: z.literal("hold_only"),
        externalBookingAllowed: z.literal(false),
    })
        .strict(),
});
export const StaffTaskActionSchema = z.object({
    ...baseActionShape,
    kind: z.literal("staff_task"),
    payload: z
        .object({
        taskVersion: z.literal(1),
        taskType: z.string().min(1).max(120),
        ownerRole: WorkforceRoleSchema.exclude(["system_agent", "auditor"]),
        dueAt: IsoDateTimeSchema,
        escalationAt: IsoDateTimeSchema,
        instructions: z.string().min(1).max(2_000),
        completionCriteria: z.array(z.string().min(1).max(500)).min(1).max(25),
        sourceActionIds: z.array(IdSchema).min(1).max(25),
    })
        .strict(),
});
export const ClinicalActionSchema = z.discriminatedUnion("kind", [
    ReferralActionSchema,
    LabOrderActionSchema,
    ImagingOrderActionSchema,
    PrescriptionActionSchema,
    DischargeInstructionActionSchema,
    FollowUpActionSchema,
    BillingSuggestionActionSchema,
    PatientPlanActionSchema,
    FormActionSchema,
    SchedulingActionSchema,
    StaffTaskActionSchema,
]);
export const ActionBundleSchema = z.object({
    id: IdSchema,
    tenantId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    version: z.number().int().positive(),
    status: ActionStatusSchema,
    createdAt: IsoDateTimeSchema,
    createdBy: z.enum(["agent", "human"]),
    summary: z.string().min(1).max(500).optional(),
    agentRunId: IdSchema.optional(),
    agentRunContentHash: Sha256Schema.optional(),
    actions: z.array(ClinicalActionSchema).min(1).max(50),
    contentHash: Sha256Schema.optional(),
});
export const ValidationResultSchema = z.object({
    valid: z.boolean(),
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    contentHash: Sha256Schema,
    issues: z.array(ValidationIssueSchema),
    validatedAt: IsoDateTimeSchema,
});
export const ApprovedActionSnapshotSchema = z.object({
    actionId: IdSchema,
    version: z.number().int().positive(),
    contentHash: Sha256Schema,
    referralRoutingAuthorizationHash: Sha256Schema.optional(),
});
export const ApprovalReceiptSchema = z.object({
    id: IdSchema,
    tenantId: IdSchema,
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    contentHash: Sha256Schema,
    actorId: IdSchema,
    actorRole: WorkforceRoleSchema.exclude(["system_agent"]),
    actorAal: z.literal("aal2"),
    approvalClass: z.enum(["user", "clinician", "epcs"]),
    approvedActionIds: z.array(IdSchema).min(1),
    approvedActions: z.array(ApprovedActionSnapshotSchema).min(1),
    policyVersion: z.string().min(1).max(80),
    approvedAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
});
export const CreateApprovalRequestSchema = z.object({
    expectedBundleVersion: z.number().int().positive(),
    expectedContentHash: Sha256Schema,
    actionIds: z.array(IdSchema).min(1).optional(),
});
export const ExecutionReceiptSchema = z.object({
    id: IdSchema,
    tenantId: IdSchema,
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    approvalReceiptId: IdSchema,
    contentHash: Sha256Schema,
    actionIds: z.array(IdSchema).min(1),
    status: z.enum(["queued", "dispatched", "acknowledged", "failed"]),
    idempotencyKey: IdempotencyKeySchema,
    queuedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    partnerReferenceIds: z.array(z.string().min(1).max(160)),
    failureCode: z.string().min(1).max(100).optional(),
});
export const ExecuteBundleRequestSchema = z.object({
    approvalReceiptId: IdSchema,
    expectedBundleVersion: z.number().int().positive(),
    idempotencyKey: IdempotencyKeySchema,
});
//# sourceMappingURL=actions.js.map